import type { UserMediaDevicePlatformError } from '@openphone/desktop-client'
import { isUserMediaDevicePlatformError } from '@openphone/desktop-client'
import { capitalize } from '@openphone/lib/string'
import { makeAutoObservable } from 'mobx'

import { logError } from '@src/lib/log'

import type UMDAPlatformStrategy from './strategies/UMDAPlatformStrategy'
import { getUserMediaDeviceErrorPrompt } from './userMediaDeviceErrorMap'

export default class UserMediaDeviceUIStore {
  constructor(private readonly strategy: UMDAPlatformStrategy) {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  async requestMicrophoneAccess() {
    const accessResult = await this.hasMicrophoneAccess()
    if (accessResult.type === 'error') {
      return accessResult
    }

    // we need to call `requestMicrophoneAccess` when the status is `granted`
    // because since Chrome 86, `enumerateDevices` will not expose device information
    // unless a previous call to `getUserMedia` was **successfully** made.
    // {@link https://groups.google.com/g/discuss-webrtc/c/qGdSM-Cs3bc/m/1V0iqPXVCwAJ}
    const result = await this.strategy.requestMicrophoneAccess()
    if (isUserMediaDevicePlatformError(result)) {
      return this.createMicrophoneAccessErrorPrompt(result)
    }

    return { type: 'success' } as const
  }

  async hasMicrophoneAccess() {
    const result = await this.strategy.hasMicrophoneAccess()
    if (isUserMediaDevicePlatformError(result)) {
      return this.createMicrophoneAccessErrorPrompt(result)
    }

    return { type: 'success' } as const
  }

  private createMicrophoneAccessErrorPrompt(error: UserMediaDevicePlatformError) {
    const prompt = getUserMediaDeviceErrorPrompt(error.subType, {
      mediaDevice: {
        capitalized: capitalize(error.mediaDevice),
        lowercase: error.mediaDevice.toLowerCase(),
      },
      originalError: error.originalError,
    })

    logError(error)

    return { type: 'error', prompt } as const
  }
}
